import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {IssueRelativeTime} from '@github-ui/ago'
import {ThreadGroupVariant, getThreadAuthor, getVariantComment, ThreadGroupVariantLabels} from '../util/classify-thread'
import {getThreadFileLabel} from '../util/thread-file-label'
import {CommentIdentity} from '../shared/CommentIdentity'
import styles from './ThreadTitle.module.css'
import {ThreadBadges} from './ThreadBadges'

function ThreadMessage({variant}: {variant: ThreadGroupVariant}) {
  return <span>&nbsp;{ThreadGroupVariantLabels[variant] || 'commented'}</span>
}

export function ThreadTitle({thread, variant}: {thread: Thread; variant: ThreadGroupVariant}) {
  const message = <ThreadMessage variant={variant} />

  // Falls back to the ghost user (login `ghost`, profile `/ghost`) when the
  // comment author no longer exists, matching the expanded thread view.
  const author = getThreadAuthor(thread, variant)

  // When the thread is code-anchored, show the file name (and lines) inline in the
  // header. The full file path is shown only once the item is expanded.
  const fileLabel = getThreadFileLabel(thread)

  // The relative time reflects the same comment whose author is shown as the
  // headline "X", matching the relative time shown in the conversation tab.
  const headlineComment = getVariantComment(thread, variant)

  return (
    <span className={styles.ThreadTitle}>
      <CommentIdentity login={author.login} linkable={author.linkable} />
      {message}
      {fileLabel.hasFile ? <span className={styles.fileName}>&nbsp;on {fileLabel.fileName}</span> : null}
      {headlineComment?.createdAt ? (
        <span className={styles.relativeTime}>
          &nbsp;
          <IssueRelativeTime datetime={headlineComment.createdAt} data-testid="thread-title-relative-time" />
        </span>
      ) : null}
      <ThreadBadges isResolved={thread.isResolved} isOutdated={thread.isOutdated} className={styles.badges} />
    </span>
  )
}
